import { AfterContentInit, Component, ContentChild, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { BannerSponsoring, SPONSORING_POSITION } from '~app/modules/storyblok/models';
import { TooltipPositions } from '~shared/modules/tooltip/models/tooltip-positions.model';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { ApplicationEventType, BannerEventActionType } from '~shared/modules/application-event/constants';
import { BehaviorSubject } from 'rxjs';
import { viewportSizeDetector } from '~app/modules/generic/helpers';
import { NUMBER_500, NUMBER_968 } from '~shared/constants';

@Component({
  selector: 'app-sponsoring-wrapper',
  templateUrl: './sponsoring-wrapper.component.html',
  styleUrls: ['./sponsoring-wrapper.component.scss'],
})
export class SponsoringWrapperComponent implements OnInit, AfterContentInit {
  @ContentChild('wrappedComponent', { static: false }) wrappedComponent!: ElementRef;

  @Input() sponsorItem: BannerSponsoring;
  @Input() isHeroBanner: boolean = false;
  @Input() customClickEvent?: any;
  @Input() customHoverEvent?: any;

  overlayOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isWrappedComponentVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  get isTooltipInLeftPosition(): boolean {
    return this.sponsorItem.position === SPONSORING_POSITION.bottomLeft;
  }

  constructor(private gtmDatalayerService: GtmDatalayerService) {}

  ngOnInit(): void {
    this.subscribeToOverlayVisibilityInMobile();
  }

  ngAfterContentInit(): void {
    this.checkForWindowResize();
    this.isWrappedComponentVisible$.next(!!this.wrappedComponent);
  }

  @HostListener('window:resize', ['$event'])
  windowResizeListener(): void {
    // @INFO: Using this instead of media observer, bc media observer did not handle this 968 viewport.
    this.checkForWindowResize();
  }

  private checkForWindowResize(): void {
    const viewportSize: string = viewportSizeDetector(window.innerWidth, true);
    this.isMobile$.next(+viewportSize < NUMBER_968);
  }

  private subscribeToOverlayVisibilityInMobile(): void {
    this.isMobile$.subscribe((isMobile: boolean) => {
      if (!isMobile) {
        this.hideOverlay();
      }
    });
  }

  showOverlay(): void {
    this.overlayOpen$.next(true);
  }

  hideOverlay(): void {
    this.overlayOpen$.next(false);
  }

  triggerTooltipClickEvent(): void {
    this.showOverlay();
    if (!this.customClickEvent) {
      this.gtmDatalayerService.gtagEvent({
        action: BannerEventActionType.SponsoringBannerClicked,
        event: GATrackingEventType.SE_SPONSORING_BANNER_CLICKED,
        label: 'sponsored banner',
        category: ApplicationEventType.Interaction,
        GA_eventname: ApplicationEventType.Interaction,
      });

      return;
    }

    this.gtmDatalayerService.gtagEvent(this.customClickEvent);
  }

  triggerTooltipHoverEvent(): void {
    if (!this.customHoverEvent) {
      this.gtmDatalayerService.gtagEvent({
        action: BannerEventActionType.SponsoringBannerHovered,
        event: GATrackingEventType.SE_SPONSORING_BANNER_HOVERED,
        label: 'sponsored banner',
        category: ApplicationEventType.Interaction,
        GA_eventname: ApplicationEventType.Interaction,
      });

      return;
    }

    this.gtmDatalayerService.gtagEvent(this.customHoverEvent);
  }

  protected readonly TooltipPositions = TooltipPositions;
}
